import React, { useState, useEffect } from 'react';
import { httpClient } from '../services/httpClient';
import { ENDPOINTS } from '../config/api.config';

const VendorFormModal = ({ isOpen, mode, vendorData, onClose, onSuccess }) => {
  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagSearch, setTagSearch] = useState('');
  const [filteredTags, setFilteredTags] = useState([]);
  const [workingDays, setWorkingDays] = useState(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);
  const [timeRange, setTimeRange] = useState({ start: '09:00', end: '18:00' });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    business_name: '',
    address: '',
    city_id: '',
    area_id: '',
    description: '',
    is_verified: false,
    working_hours: '',
    lat: '',
    lng: '',
  });

  const daysList = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  useEffect(() => {
    if (isOpen) {
      loadInitialData();
      if (mode === 'edit' && vendorData) {
        loadVendorData();
      } else {
        resetForm();
      }
    }
  }, [isOpen, mode]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [citiesRes, tagsRes] = await Promise.all([
        httpClient.get(ENDPOINTS.CITIES),
        httpClient.get(ENDPOINTS.TAGS),
      ]);

      let citiesData = Array.isArray(citiesRes) ? citiesRes : (Array.isArray(citiesRes?.data) ? citiesRes.data : []);
      let tagsData = Array.isArray(tagsRes) ? tagsRes : (Array.isArray(tagsRes?.data) ? tagsRes.data : []);

      setCities(citiesData);
      setAllTags(tagsData);
    } catch (err) {
      setError('Failed to load form data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadVendorData = async () => {
    try {
      const response = await httpClient.get(`${ENDPOINTS.ADMIN_API}/vendors/${vendorData.id}`);
      const vendor = response?.data || response;

      setFormData({
        name: vendor.name || '',
        email: vendor.email || '',
        phone: vendor.phone || '',
        business_name: vendor.business_name || '',
        address: vendor.address || '',
        city_id: vendor.city_id || '',
        area_id: vendor.area_id || '',
        description: vendor.description || '',
        is_verified: vendor.is_verified || false,
        working_hours: vendor.working_hours || '',
        lat: vendor.lat || '',
        lng: vendor.lng || '',
      });

      if (vendor.tags) {
        setSelectedTags(Array.isArray(vendor.tags) ? vendor.tags : []);
      }

      if (vendor.city_id) {
        fetchAreas(vendor.city_id);
      }

      if (vendor.working_hours) {
        const hoursMatch = vendor.working_hours.match(/(\w+(?:,\s*\w+)*)\s*\((\d{2}:\d{2})-(\d{2}:\d{2})\)/);
        if (hoursMatch) {
          setWorkingDays(hoursMatch[1].split(', '));
          setTimeRange({ start: hoursMatch[2], end: hoursMatch[3] });
        }
      }
    } catch (err) {
      setError('Failed to load vendor details');
      console.error(err);
    }
  };

  const fetchAreas = async (cityId) => {
    try {
      const response = await httpClient.get(`${ENDPOINTS.AREAS}?city_id=${cityId}`);
      let areasData = Array.isArray(response) ? response : (Array.isArray(response?.data) ? response.data : []);
      setAreas(areasData);
    } catch (err) {
      console.error('Error fetching areas:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      phone: '',
      business_name: '',
      address: '',
      city_id: '',
      area_id: '',
      description: '',
      is_verified: false,
      working_hours: '',
      lat: '',
      lng: '',
    });
    setSelectedTags([]);
    setTagSearch('');
    setFilteredTags([]);
    setWorkingDays(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);
    setTimeRange({ start: '09:00', end: '18:00' });
    setAreas([]);
    setError('');
  };

  useEffect(() => {
    if (tagSearch.trim().length > 0) {
      const search = tagSearch.toLowerCase();
      const filtered = allTags.filter(tag =>
        tag && tag.name && tag.name.toLowerCase().includes(search) &&
        !selectedTags.some(selected => selected && selected.id === tag.id)
      );
      setFilteredTags(filtered.slice(0, 5));
    } else {
      setFilteredTags([]);
    }
  }, [tagSearch, allTags, selectedTags]);

  const toggleDay = (day) => {
    setWorkingDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  };

  const handleTagSelect = (tag) => {
    setSelectedTags(prev => [...prev, tag]);
    setTagSearch('');
  };

  const handleTagRemove = (tag) => {
    setSelectedTags(prev => prev.filter(t => (t.id ? t.id !== tag.id : t.name !== tag.name)));
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const tagName = tagSearch.trim();
      if (tagName && !selectedTags.some(t => t.name.toLowerCase() === tagName.toLowerCase())) {
        const existingTag = allTags.find(t => t.name.toLowerCase() === tagName.toLowerCase());
        if (existingTag) {
          handleTagSelect(existingTag);
        } else {
          setSelectedTags(prev => [...prev, { id: null, name: tagName }]);
          setTagSearch('');
        }
      }
    }
  };

  const handleCityChange = async (cityId) => {
    setFormData({ ...formData, city_id: cityId, area_id: '' });
    if (cityId) {
      fetchAreas(cityId);
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!formData.email.includes('@')) {
      setError('Valid email is required');
      return false;
    }
    if (!formData.phone.trim()) {
      setError('Phone is required');
      return false;
    }
    if (mode === 'add' && !formData.password.trim()) {
      setError('Password is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    const hoursFormatted = workingDays.length > 0
      ? `${workingDays.join(', ')} (${timeRange.start}-${timeRange.end})`
      : 'Closed / Appointment Only';

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        business_name: formData.business_name,
        address: formData.address,
        city_id: formData.city_id,
        area_id: formData.area_id,
        description: formData.description,
        is_verified: formData.is_verified,
        working_hours: hoursFormatted,
        lat: formData.lat,
        lng: formData.lng,
        tags: selectedTags.map(tag => ({ id: tag.id || null, name: tag.name })),
      };

      if (mode === 'add') {
        if (!formData.password.trim()) {
          setError('Password is required');
          setSubmitting(false);
          return;
        }
        payload.password = formData.password;
        payload.user_type = 'vendor';
        await httpClient.post(`${ENDPOINTS.ADMIN_API}/vendors`, payload);
      } else {
        if (formData.password.trim()) {
          payload.password = formData.password;
        }
        await httpClient.put(`${ENDPOINTS.ADMIN_API}/vendors/${vendorData.id}`, payload);
      }

      httpClient.clearCache();
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.title}>{mode === 'add' ? '➕ Add Vendor' : '✏️ Edit Vendor'}</h2>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {error && <div style={styles.errorBanner}>{error}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGrid}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Full Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                style={styles.input}
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                style={styles.input}
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Phone *</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                style={styles.input}
              />
            </div>

            {mode === 'add' && (
              <div style={styles.inputGroup}>
                <label style={styles.label}>Password *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={mode === 'add'}
                  style={styles.input}
                />
              </div>
            )}

            {mode === 'edit' && (
              <div style={styles.inputGroup}>
                <label style={styles.label}>New Password (leave empty to keep current)</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  style={styles.input}
                />
              </div>
            )}
          </div>

          <div style={styles.formGrid}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Business Name</label>
              <input
                type="text"
                value={formData.business_name}
                onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                style={styles.input}
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>City</label>
              <select
                value={formData.city_id}
                onChange={(e) => handleCityChange(e.target.value)}
                style={styles.input}
              >
                <option value="">Select City</option>
                {cities.map(city => (
                  <option key={city.id} value={city.id}>{city.name}</option>
                ))}
              </select>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Area</label>
              <select
                value={formData.area_id}
                onChange={(e) => setFormData({ ...formData, area_id: e.target.value })}
                disabled={!formData.city_id}
                style={styles.input}
              >
                <option value="">Select Area</option>
                {areas.map(area => (
                  <option key={area.id} value={area.id}>{area.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Address</label>
            <textarea
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              rows={2}
              style={{...styles.input, fontFamily: 'inherit'}}
            />
          </div>

          <div style={styles.formGrid}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Latitude</label>
              <input
                type="number"
                value={formData.lat}
                onChange={(e) => setFormData({ ...formData, lat: e.target.value })}
                step="0.000001"
                style={styles.input}
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Longitude</label>
              <input
                type="number"
                value={formData.lng}
                onChange={(e) => setFormData({ ...formData, lng: e.target.value })}
                step="0.000001"
                style={styles.input}
              />
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              style={{...styles.input, fontFamily: 'inherit'}}
            />
          </div>

          <div style={{ ...styles.inputGroup, marginTop: '16px' }}>
            <label style={styles.label}>Working Days</label>
            <div style={styles.daysContainer}>
              {daysList.map(day => (
                <button
                  key={day}
                  type="button"
                  onClick={() => toggleDay(day)}
                  style={{
                    ...styles.dayBtn,
                    background: workingDays.includes(day) ? '#4f46e5' : 'white',
                    color: workingDays.includes(day) ? 'white' : '#1e293b',
                    border: `1px solid ${workingDays.includes(day) ? '#4f46e5' : '#e2e8f0'}`,
                  }}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>

          <div style={styles.formGrid}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Opens At</label>
              <input
                type="time"
                value={timeRange.start}
                onChange={(e) => setTimeRange({ ...timeRange, start: e.target.value })}
                style={styles.input}
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Closes At</label>
              <input
                type="time"
                value={timeRange.end}
                onChange={(e) => setTimeRange({ ...timeRange, end: e.target.value })}
                style={styles.input}
              />
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Tags</label>
            <div style={styles.tagSearchBox}>
              <input
                type="text"
                placeholder="Search or type tag name..."
                value={tagSearch}
                onChange={(e) => setTagSearch(e.target.value)}
                onKeyDown={handleTagInputKeyDown}
                style={{...styles.input, marginBottom: '8px'}}
              />
              {filteredTags.length > 0 && (
                <div style={styles.suggestionBox}>
                  {filteredTags.map(tag => (
                    <div
                      key={tag.id}
                      onClick={() => handleTagSelect(tag)}
                      style={styles.suggestionItem}
                    >
                      {tag.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={styles.tagsContainer}>
              {selectedTags.map(tag => (
                <span key={tag.id || tag.name} style={styles.tag}>
                  {tag.name}
                  <button
                    type="button"
                    onClick={() => handleTagRemove(tag)}
                    style={{ marginLeft: '6px', background: 'none', border: 'none', cursor: 'pointer', color: '#4f46e5' }}
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.is_verified}
                onChange={(e) => setFormData({ ...formData, is_verified: e.target.checked })}
                style={{ cursor: 'pointer' }}
              />
              Mark as Verified
            </label>
          </div>

          <div style={styles.actions}>
            <button type="button" onClick={onClose} style={styles.cancelBtn}>Cancel</button>
            <button type="submit" disabled={submitting} style={styles.submitBtn}>
              {submitting ? 'Submitting...' : (mode === 'add' ? 'Create Vendor' : 'Update Vendor')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '32px',
    maxWidth: '700px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: '2px solid #f1f5f9',
  },
  title: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#0f172a',
    margin: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#64748b',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  input: {
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '14px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    color: '#0f172a',
    backgroundColor: '#fff',
  },
  label: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1e293b',
  },
  daysContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  dayBtn: {
    padding: '6px 12px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
    transition: '0.2s',
  },
  tagSearchBox: {
    position: 'relative',
  },
  suggestionBox: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    zIndex: 10,
    maxHeight: '200px',
    overflowY: 'auto',
  },
  suggestionItem: {
    padding: '10px 12px',
    cursor: 'pointer',
    fontSize: '13px',
    color: '#1e293b',
    borderBottom: '1px solid #f1f5f9',
    transition: '0.15s',
    backgroundColor: '#fff',
  },
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '8px',
  },
  tag: {
    backgroundColor: '#eef2ff',
    color: '#4f46e5',
    padding: '6px 12px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  errorBanner: {
    backgroundColor: '#fef2f2',
    color: '#b91c1c',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '14px',
    border: '1px solid #fee2e2',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '24px',
    paddingTop: '16px',
    borderTop: '1px solid #f1f5f9',
  },
  cancelBtn: {
    padding: '10px 20px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    backgroundColor: '#fff',
    color: '#64748b',
    cursor: 'pointer',
    fontWeight: 600,
    transition: '0.2s',
  },
  submitBtn: {
    padding: '10px 20px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#4f46e5',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: 700,
    transition: '0.2s',
  },
};

export default VendorFormModal;
